import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Globe } from 'lucide-react';
import organizationsService from '../../services/organizationsService';

// ── Domains ──────────────────────────────────────────────────────────────────

export function DomainsTab({ org, canEdit }) {
  const [domains, setDomains] = useState([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setDomains(await organizationsService.getDomains(org.id));
    } catch (e) {
      setError(e.message);
    }
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!draft.trim()) return;
    setError('');
    try {
      await organizationsService.addDomain(org.id, draft.trim());
      setDraft('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.errors?.domain?.[0] || 'Could not add that domain.');
    }
  };

  const verify = async (d) => {
    setBusy(d.id);
    setError('');
    try {
      await organizationsService.verifyDomain(org.id, d.id);
      await load();
    } catch (e) {
      // 409 means the record is simply not published yet, and the fix is in
      // DNS rather than in this form — so it is phrased as "not yet", not as
      // a failure the user did something wrong to cause.
      setError(
        e?.response?.status === 409
          ? 'That TXT record was not found yet. DNS changes can take a while to appear.'
          : e?.response?.data?.error || 'Could not verify that domain.'
      );
    } finally {
      setBusy(null);
    }
  };

  const remove = async (d) => {
    setError('');
    try {
      await organizationsService.removeDomain(org.id, d.id);
      await load();
    } catch {
      setError('Could not remove that domain.');
    }
  };

  return (
    <div className="org-form">
      <p className="org-hint">
        Verifying a domain lets people with an address on it join automatically. It proves
        you control the domain — until then, the claim grants nothing.
      </p>

      {canEdit && (
        <div className="org-invite">
          <input
            className="org-input"
            value={draft}
            placeholder="acme.com"
            aria-label="Add a domain"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
          <button type="button" className="org-btn" disabled={!draft.trim()} onClick={add}>
            <Plus size={13} /> Claim
          </button>
        </div>
      )}

      {error && <p className="org-error">{error}</p>}

      {domains.length === 0 && <p className="org-hint">No domains claimed yet.</p>}

      {domains.map((d) => (
        <div key={d.id} className="org-row">
          <Globe size={16} className="org-row-icon" aria-hidden />
          <div className="org-row-texts">
            <span className="org-row-name">
              {d.domain}{' '}
              <span className={`orgx-role${d.verified ? ' orgx-role--owner' : ''}`}>
                {d.verified ? 'verified' : 'unverified'}
              </span>
            </span>
            {!d.verified && (
              // The token is what they publish, so it has to be selectable
              // rather than merely displayed — a value you cannot copy is a
              // value you retype wrongly.
              <span className="org-row-sub org-token">
                Add a TXT record on <strong>{d.domain}</strong> with the value{' '}
                <code>{d.verification_token}</code>
              </span>
            )}
          </div>
          {canEdit && !d.verified && (
            <button
              type="button"
              className="org-btn"
              disabled={busy === d.id}
              onClick={() => verify(d)}
            >
              {busy === d.id ? 'Checking…' : 'Verify'}
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="org-icon-btn"
              aria-label={`Remove ${d.domain}`}
              onClick={() => remove(d)}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
